/**
 * Strategy Optimization Documentation
 *
 * How to use parameter optimization, WFA, and Adaptive Flow from the platform UI.
 */
import type { DocPage } from '../types';

export const strategyOptimizationDocs: DocPage[] = [
  {
    id: 'strategy-optimization/overview',
    title: 'Optimization Overview',
    category: 'strategy-optimization',
    tags: ['optimization', 'parameters', 'search', 'plateau', 'stability'],
    version: '2.0',
    related: ['strategy-optimization/running-optimization', 'strategy-optimization/walk-forward-analysis', 'params/optimization'],
    order: 1,
    description: 'Understanding parameter optimization and the validation pipeline',
    content: `# Optimization Overview

Optimization searches your strategy's parameter space to find values that produce good, **stable** results — not just the highest return on historical data.

## Why Optimize

A strategy with hard-coded parameters is a guess. Optimization replaces guessing with a systematic search that reveals:

- Which parameter regions produce consistent results
- Whether the strategy is robust or fragile
- How sensitive the results are to small parameter changes

## The Danger of Overfitting

The biggest risk in optimization is [overfitting](/docs/concepts/overfitting) — finding parameters that look great on historical data but fail on new data.

Quanthop addresses this through:

- **[Plateau detection](/docs/concepts/parameter-stability)** — Identifying broad stable regions rather than single peak values
- **Stability scoring** — Measuring how consistent a parameter region is
- **[Walk-Forward Analysis](/docs/concepts/walk-forward-analysis)** — Testing on data the optimizer never saw
- **Cross-asset validation** — Checking if the idea works beyond one symbol

## Optimization Pipeline

The recommended workflow has three stages:

1. **Parameter search** — Find stable regions in the parameter space
2. **Walk-Forward Analysis** — Validate with rolling out-of-sample windows
3. **Adaptive Flow** — Monitor the strategy after deployment

Each stage filters out strategies that are not robust enough for the next.

## Optimization Targets

You can optimize for different metrics:

| Target | Description |
|--------|-------------|
| **Sharpe Ratio** | Risk-adjusted return (recommended default) |
| **Profit Factor** | Gross profit / gross loss |
| **Total Return** | Net percentage return |
| **Win Rate** | Percentage of profitable trades |
| **Calmar Ratio** | Return relative to worst drawdown |

Sharpe Ratio is usually the best target because it penalizes high-variance results.

## Next Steps

- [Running Optimization](/docs/strategy-optimization/running-optimization) — Step-by-step optimization guide
- [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) — Out-of-sample validation
- [Adaptive Flow](/docs/strategy-optimization/adaptive-flow) — Post-deployment monitoring
`,
  },
  {
    id: 'strategy-optimization/running-optimization',
    title: 'Running Optimization',
    category: 'strategy-optimization',
    tags: ['optimization', 'run', 'configure', 'parameter search', 'grid', 'ui'],
    version: '2.0',
    related: ['strategy-optimization/overview', 'strategy-optimization/reading-optimization-results', 'params/defining'],
    order: 2,
    description: 'Configuring and running parameter optimization from the UI',
    content: `# Running Optimization

Parameter optimization is available in the **Optimization** tab of the Research Lab right dock.

## Prerequisites

Before running optimization, your strategy needs:

- At least one parameter defined in the \`define()\` function with \`min\` and \`max\` bounds
- A working backtest that completes without errors

## Setting Up Parameter Ranges

Each parameter defined in \`define()\` appears in the optimization panel with:

| Field | Description |
|-------|-------------|
| **Min** | Minimum value to search |
| **Max** | Maximum value to search |
| **Step** | Increment between values |
| **Include** | Whether to optimize this parameter (toggle) |

Parameters without explicit bounds in \`define()\` will use their defaults.

### Example

If your strategy defines:

\`\`\`javascript
function define(ctx) {
  ctx.param('period', { type: 'int', default: 14, min: 5, max: 50 });
  ctx.param('threshold', { type: 'float', default: 0.5, min: 0.1, max: 2.0 });
}
\`\`\`

The optimizer will search across all combinations of \`period\` (5 to 50) and \`threshold\` (0.1 to 2.0).

## Selecting the Target Metric

Choose what the optimizer should maximize. Sharpe Ratio is the default and usually the best choice because it balances return against risk.

## Running the Job

Click **Run Optimization**. The job runs in the background — you can continue working while it completes. Progress is shown via a real-time progress indicator.

Optimization jobs can take from seconds to several minutes depending on the parameter space size. The platform will estimate the number of combinations before you start.

## Baseline Scan

For broader validation, use **Baseline Scan** to test your strategy across multiple asset groups simultaneously. This reveals whether your strategy idea works on one symbol or generalizes across markets.

## Next Steps

- [Reading Results](/docs/strategy-optimization/reading-optimization-results) — Understanding optimization output
- [Parameter Groups](/docs/params/groups) — Organizing complex parameter sets
`,
  },
  {
    id: 'strategy-optimization/reading-optimization-results',
    title: 'Reading Optimization Results',
    category: 'strategy-optimization',
    tags: ['optimization', 'results', 'plateau', 'stability', 'heatmap', 'clusters'],
    version: '2.0',
    related: ['strategy-optimization/running-optimization', 'strategy-optimization/walk-forward-analysis'],
    order: 3,
    description: 'Interpreting optimization results, plateaus, and stability scores',
    content: `# Reading Optimization Results

After optimization completes, the results show more than just the best parameters. They reveal the **landscape** of your strategy's parameter space.

## Result Components

### Parameter Surface

A visual representation of how different parameter values affect the target metric. Look for:

- **Plateaus** — Broad flat regions where many nearby parameter values produce similar results. These are the most robust zones.
- **Sharp peaks** — Narrow spikes where only one exact combination works. These are [overfitted](/docs/concepts/overfitting) and unreliable.
- **Valleys** — Regions where the strategy fails. Understanding where it breaks is as important as where it works.

### Stability Score

Each parameter cluster receives a stability score measuring:

- How consistent the metric values are within the region
- How large the region is (broader is more stable)
- How far the metrics drop at the edges

Higher stability scores indicate more robust parameter regions.

### Top Results Table

The results table shows the best parameter combinations ranked by your target metric, along with:

- All performance metrics (return, Sharpe, drawdown, etc.)
- Stability score for the parameter region
- Trade count and win rate

## Choosing Parameters

**Do not simply pick the row with the highest return.** Instead:

1. Look for clusters with high stability scores
2. Within those clusters, prefer the center of the plateau (not the edges)
3. Check that the trade count is sufficient (ideally 30+)
4. Verify that the drawdown is acceptable

A parameter set with slightly lower returns but higher stability will almost always perform better on unseen data.

## Next Steps

- [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) — Test your chosen parameters out-of-sample
- [Optimization Overview](/docs/strategy-optimization/overview) — The full validation pipeline
`,
  },
  {
    id: 'strategy-optimization/walk-forward-analysis',
    title: 'Walk-Forward Analysis',
    category: 'strategy-optimization',
    tags: ['wfa', 'walk-forward', 'out-of-sample', 'validation', 'rolling windows'],
    version: '2.0',
    related: ['strategy-optimization/overview', 'strategy-optimization/running-optimization', 'strategy-optimization/adaptive-flow', 'runs/wfa'],
    order: 4,
    description: 'Validating strategies with rolling out-of-sample windows',
    content: `# Walk-Forward Analysis

Walk-Forward Analysis (WFA) is the most important validation step. It tests whether optimization results hold up on data the optimizer has never seen.

## How WFA Works

WFA divides your data into rolling windows:

1. **In-sample window** — The optimizer finds the best parameters on this period
2. **Out-of-sample window** — The strategy runs with those parameters on the next unseen period
3. **Roll forward** — The window shifts, and the process repeats

Each out-of-sample period is a genuine forward test. The strategy cannot see this data during optimization.

## Why This Matters

A strategy that performs well in-sample but fails out-of-sample is [overfitted](/docs/concepts/overfitting). WFA catches this before you risk real capital.

The out-of-sample results are the ones that matter. In-sample results show what the optimizer found; out-of-sample results show whether those findings are real.

## Configuring WFA

In the **WFA** tab of the Research Lab:

| Setting | Description |
|---------|-------------|
| **Window size** | Length of each in-sample optimization window |
| **Out-of-sample size** | Length of each out-of-sample test period |
| **Step size** | How far to roll the window each iteration |
| **Target metric** | What to optimize for in each window |

### Window Size Guidelines

- Too small: Not enough data for meaningful optimization
- Too large: Fewer windows, less validation
- A good starting point is 60-70% in-sample, 30-40% out-of-sample

## Reading WFA Results

After WFA completes, you see:

- **Window-by-window breakdown** — Performance for each in-sample and out-of-sample period
- **Aggregate out-of-sample metrics** — Combined performance across all forward tests
- **Consistency score** — How stable the results are across windows
- **Parameter drift** — Whether the optimizer picks similar parameters in each window or wildly different ones

### Key Indicators

- **Positive out-of-sample returns** — The strategy has predictive value
- **Consistent parameters** — The optimizer finds similar values each time
- **In-sample / out-of-sample alignment** — Forward test results resemble optimization results

### Warning Signs

- Out-of-sample results are significantly worse than in-sample
- Parameters change dramatically between windows
- Some windows are highly profitable while others lose money

## Multi-Asset WFA

Pack WFA runs Walk-Forward Analysis across multiple assets simultaneously, using a common parameter set. This is the strongest test of strategy robustness — if the same parameters work across different markets, the idea is likely genuine.

## Saving WFA Results

WFA results can be saved for later review and comparison. Saved results are accessible from the **WFA Results** page in the main navigation.

## Next Steps

- [Adaptive Flow](/docs/strategy-optimization/adaptive-flow) — Monitor strategy performance post-deployment
- [WFA Technical Details](/docs/runs/wfa) — Implementation details of the WFA engine
`,
  },
  {
    id: 'strategy-optimization/adaptive-flow',
    title: 'Adaptive Flow',
    category: 'strategy-optimization',
    tags: ['adaptive flow', 'monitoring', 'reoptimization', 'deployment', 'continuous'],
    version: '2.0',
    related: ['strategy-optimization/walk-forward-analysis', 'strategy-optimization/overview', 'runs/adaptive-flow'],
    order: 5,
    description: 'Continuous monitoring and reoptimization after strategy deployment',
    content: `# Adaptive Flow

Adaptive Flow is the post-deployment monitoring system. After a strategy passes WFA validation, Adaptive Flow tracks whether it continues to perform as expected.

## Why Monitor

Markets change. A strategy that passed WFA six months ago may no longer be effective. Adaptive Flow detects degradation early, before significant losses accumulate.

## How It Works

Adaptive Flow continuously:

1. **Tracks live performance** against historical expectations
2. **Detects regime changes** that may affect the strategy
3. **Flags degradation** when out-of-sample results diverge from validated benchmarks
4. **Suggests reoptimization** when the strategy needs new parameters

## Accessing Adaptive Flow

Adaptive Flow is available from:

- The **WFA Results** page — click on a saved WFA result to enter monitoring mode
- Direct URL — each Adaptive Flow session has a unique link

## Dashboard Metrics

The Adaptive Flow dashboard shows:

| Metric | Description |
|--------|-------------|
| **Current performance** | Recent returns vs. historical average |
| **Regime status** | Whether current market conditions match the validation period |
| **Degradation score** | How far performance has drifted from expectations |
| **Reoptimization signal** | Whether the strategy needs new parameters |

## Reoptimization

When Adaptive Flow detects significant degradation, it can trigger a reoptimization cycle:

1. Run a new optimization on recent data
2. Validate with a fresh WFA pass
3. Compare new parameters against the current set
4. Update if the new parameters show improvement

This creates a continuous feedback loop: validate, deploy, monitor, reoptimize.

## Next Steps

- [Optimization Overview](/docs/strategy-optimization/overview) — The full research pipeline
- [Walk-Forward Analysis](/docs/strategy-optimization/walk-forward-analysis) — WFA validation
`,
  },
];
